'use client';

import { useMemo } from 'react';
import { PanelDialog } from '@/components/ui/layout/PanelDialog';
import ContactsClient from '@/app/dashboard/contacts/ContactsClient';
import type { GetContactsPayload } from '@/components/features/reports/get-contacts-types';

type InstantContactsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payload: GetContactsPayload | null;
  hasPaidAccess?: boolean;
};

export function InstantContactsDialog({
  open,
  onOpenChange,
  payload,
  hasPaidAccess,
}: InstantContactsDialogProps) {
  const initialCompanyFilter = useMemo(() => {
    if (!payload) return [];

    const normalizedCompanyName = payload.companyName?.trim();
    if (normalizedCompanyName) {
      return [normalizedCompanyName];
    }

    if (payload.companyId !== undefined && payload.companyId !== null) {
      const companyIdValue = String(payload.companyId).trim();
      if (companyIdValue.length > 0) {
        return [companyIdValue];
      }
    }

    return [];
  }, [payload]);

  const dialogTitle = useMemo(() => {
    if (payload?.companyName) {
      return `Instant contacts for ${payload.companyName}`;
    }
    return 'Instant contacts lookup';
  }, [payload]);

  return (
    <PanelDialog
      open={open}
      onOpenChange={onOpenChange}
      title={dialogTitle}
      description="Run the same instant lookup experience as the contact search page, prefilled for this company."
      desktopHeaderVariant="compact"
      desktopMaxWidth="sm:max-w-[min(96vw,1400px)]"
      desktopHeight="sm:h-[90vh]"
    >
      <div className="-mx-4 -mb-6 -mt-2 min-h-full bg-background sm:-mx-7 sm:-mb-8 sm:-mt-3">
        <ContactsClient
          key={`${open ? 'open' : 'closed'}:${initialCompanyFilter.join('|')}`}
          initialContacts={[]}
          initialViewMode="lookup"
          lookupRedirectPath="/d/contacts/search"
          initialLookupFilters={{ companies: initialCompanyFilter }}
          autoRunLookupOnInit
          autoRunLookupWhen={open}
          embedded
          lookupOnly
          lookupResultsVariant="job_details_list"
          hasLookupPaidAccessOverride={hasPaidAccess}
        />
      </div>
    </PanelDialog>
  );
}
